import { useCallback, useRef } from 'react'

import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { EngagementType } from '../../graphqlEnums'

export const ReplyToEngagementMutationGQL = graphql(/* GraphQL */ `
  mutation ReplyToEngagement($input: ReplyToEngagementInput!) {
    replyToEngagement(input: $input) {
      __typename
      ... on ReplyToEngagementSuccess {
        engagement {
          __typename
          id
          status
          parentId
          repliedAt
          updatedAt
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

/**
 * The engagement returned by a successful reply — the parent item updated to
 * `replied`. Narrowed off the mutation union so callers don't re-narrow.
 */
type RepliedEngagement = Extract<
  ResultOf<typeof ReplyToEngagementMutationGQL>['replyToEngagement'],
  { __typename: 'ReplyToEngagementSuccess' }
>['engagement']

type ReplyToEngagementArgs = {
  engagementId: string
  engagementType: EngagementType
  text: string
}

type ReplyToEngagementResult = {
  success: boolean
  data?: RepliedEngagement
  error?: string
  errorType?: string
  apolloError?: ErrorLike
}

type UseReplyToEngagementResult = {
  replyToEngagement: (
    args: ReplyToEngagementArgs,
  ) => Promise<ReplyToEngagementResult>
  loading: boolean
  error?: Error | null
}

/**
 * Wraps the unified `replyToEngagement` mutation. Kind-agnostic — accepts
 * `mention` or `comment` via `engagementType` (today only `mention` is
 * implemented BE-side; `comment` throws `InvalidValue`).
 *
 * Mirrors `useReplyToComment`'s return shape (`{ success, data?, error?,
 * errorType?, apolloError? }`) and sits alongside `useDismissEngagement` /
 * `useUndismissEngagement` so the eventual comments cutover slots in cleanly.
 *
 * The returned `engagement` is the parent updated to `replied`, not the new
 * reply — `Mention` exposes no `lastReplies` (unlike `Comment`), so callers
 * thread the sent reply into local state themselves.
 */
export const useReplyToEngagement = (): UseReplyToEngagementResult => {
  // useTypedMutation catches all thrown errors internally and never re-throws,
  // so hookError (React state) is always stale by the time we check it inside
  // an async callback. Capture the ApolloError synchronously via onError
  // instead — same pattern as `useReplyToComment`.
  const latestApolloErrorRef = useRef<ErrorLike | null>(null)
  const [mutate, { loading, error: hookError }] = useTypedMutation(
    ReplyToEngagementMutationGQL,
    (data) => data.replyToEngagement,
    {
      successTypename: 'ReplyToEngagementSuccess',
      // Don't write to Apollo cache so query watchers don't re-render on every
      // reply. New replies are tracked in local component state instead.
      fetchPolicy: 'no-cache',
      // A reply moves the mention out of `unreplied`; refresh the counts so the
      // tab number/dot and general counter reflect it (mentions have no Pusher).
      refetchQueries: ['MentionCounts'],
      onError: (error) => {
        latestApolloErrorRef.current = error
      },
    },
  )

  const replyToEngagement = useCallback(
    async ({
      engagementId,
      engagementType,
      text,
    }: ReplyToEngagementArgs): Promise<ReplyToEngagementResult> => {
      // Reset so onError can capture the ApolloError from this invocation
      latestApolloErrorRef.current = null

      const result = await mutate({
        variables: { input: { engagementId, engagementType, text } },
      })

      if (!result.success) {
        return {
          success: false,
          error: result.error.message,
          errorType: result.error.__typename,
          apolloError: latestApolloErrorRef.current ?? undefined,
        }
      }

      return { success: true, data: result.data.engagement }
    },
    [mutate],
  )

  return {
    replyToEngagement,
    loading,
    error:
      hookError && 'message' in hookError ? new Error(hookError.message) : null,
  }
}
